/**
 * Multi-select checkbox list for binding capabilities to an agent.
 *
 * Source of truth is `GET /api/v1/capabilities` — only `status: "active"`
 * capabilities are offered for selection. Any currently-selected ids that
 * aren't in the live list (broken / uninstalled wheels) are still rendered
 * as a tagged "missing" row so the operator can deselect them.
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { listCapabilities } from '@optum/api-client/endpoints/capabilities';

import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../ui/AsyncState.js';
import { qk } from '../../lib/query-keys.js';
import { Checkbox } from '../../ui/Checkbox.js';
import { DividedList } from '../../ui/DividedList.js';

export interface CapabilityMultiSelectProps {
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
}

export function CapabilityMultiSelect({ value, onChange, disabled }: CapabilityMultiSelectProps) {
  const query = useQuery({ queryKey: qk.capabilities(), queryFn: listCapabilities });

  if (query.isPending) return <LoadingBlock />;
  if (query.isError) {
    return (
      <ErrorBlock
        message={query.error instanceof Error ? query.error.message : 'Failed to load capabilities'}
      />
    );
  }

  const active = (query.data ?? []).filter((c) => c.status === 'active');
  const knownIds = new Set(active.map((c) => c.id));
  const orphans = value.filter((id) => !knownIds.has(id));

  function toggle(id: string, checked: boolean) {
    if (checked) onChange([...value, id]);
    else onChange(value.filter((v) => v !== id));
  }

  if (active.length === 0 && orphans.length === 0) {
    return <EmptyBlock>No capabilities installed yet — install one via Plugins.</EmptyBlock>;
  }

  return (
    <DividedList
      as="div"
      style={{ borderRadius: 'var(--radius-chip)', border: '1px solid var(--border-hairline)' }}
    >
      {active.map((cap) => {
        const checked = value.includes(cap.id);
        return (
          <label
            key={cap.id}
            style={{ display: 'flex', alignItems: 'center', gap: 11, padding: 11 }}
          >
            <Checkbox
              checked={checked}
              onChange={(checked) => toggle(cap.id, checked)}
              disabled={disabled}
            />
            <div style={{ flex: '1 1 0%' }}>
              <div
                style={{
                  fontSize: 'var(--text-body-md)',
                  fontWeight: 'var(--weight-medium)',
                  color: 'var(--text-strong)',
                }}
              >
                {cap.name} <span style={{ color: 'var(--text-muted)' }}>({cap.version})</span>
              </div>
              <div
                style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}
                className="mono"
              >
                {cap.id}
              </div>
            </div>
          </label>
        );
      })}
      {orphans.map((id) => (
        <label key={id} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: 11 }}>
          <Checkbox checked onChange={(checked) => toggle(id, checked)} disabled={disabled} />
          <div style={{ flex: '1 1 0%' }}>
            <div
              style={{
                fontSize: 'var(--text-body-md)',
                fontWeight: 'var(--weight-medium)',
                color: 'var(--danger)',
              }}
            >
              {id}
            </div>
            <div style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>
              Capability is unavailable (wheel missing or marked broken).{' '}
              <Link to="/plugins" style={{ textDecoration: 'underline' }}>
                Manage in Plugins
              </Link>{' '}
              · uncheck to remove from this agent.
            </div>
          </div>
        </label>
      ))}
    </DividedList>
  );
}
